import { motion } from "framer-motion";
import { FileText, MonitorDown, Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppProviders";

// Serverless config — real release asset URLs
const MANUAL_URL = "/USER_MANUAL_RELEASE_v1.0.0.pdf";
const INSTALLER_URL = "/SecureMaintPro-Setup-1.0.0.exe";

const Downloads = () => {
  const { t, setTermsOpen } = useApp();

  const handleDownload = (label, url) => {
    if (url && url !== "#") window.open(url, "_blank", "noopener");
    toast.success(t.downloads.started, {
      description: label,
      duration: 3500,
    });
  };

  const copyChecksum = (value) => {
    navigator.clipboard?.writeText(value);
    toast.success(t.downloads.copied, { duration: 2500 });
  };

  const items = [
    {
      key: "manual",
      icon: FileText,
      data: t.downloads.manual,
      url: MANUAL_URL,
      testid: "download-manual",
    },
    {
      key: "installer",
      icon: MonitorDown,
      data: t.downloads.installer,
      url: INSTALLER_URL,
      testid: "download-installer",
    },
  ];

  return (
    <section
      id="downloads"
      data-testid="downloads-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {t.downloads.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {t.downloads.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {t.downloads.subtitle}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`${item.testid}-card`}
                className="flex flex-col sm:flex-row sm:items-center gap-6 rounded-sm border border-border bg-card p-8"
              >
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-sm border border-blue-600/30 bg-blue-600/10 text-blue-500">
                  <Icon className="h-7 w-7" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-medium tracking-tight">
                    {item.data.title}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {item.data.desc}
                  </p>
                  <p className="mt-2 font-mono text-xs uppercase tracking-wide text-muted-foreground/70">
                    {item.data.meta}
                  </p>
                  {item.data.checksum && (
                    <div className="mt-3 flex flex-col gap-2">
                      <span
                        data-testid="installer-platform"
                        className="inline-flex w-fit items-center rounded-sm border border-border bg-secondary/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground"
                      >
                        {item.data.platform}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground/70">
                          {item.data.checksumLabel}
                        </span>
                        <code
                          data-testid="installer-checksum"
                          className="max-w-[180px] truncate font-mono text-[10px] text-muted-foreground/60 sm:max-w-[240px]"
                          title={item.data.checksum}
                        >
                          {item.data.checksum}
                        </code>
                        <button
                          onClick={() => copyChecksum(item.data.checksum)}
                          data-testid="installer-checksum-copy"
                          aria-label={item.data.checksumLabel}
                          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm border border-border text-muted-foreground transition-colors hover:border-blue-600/50 hover:text-blue-500"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleDownload(item.data.title, item.url)}
                  data-testid={item.testid}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-sm bg-blue-600 px-5 text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-200 whitespace-nowrap"
                >
                  <Download className="h-4 w-4" />
                  {item.data.btn}
                </button>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-6 font-mono text-xs text-muted-foreground/70">
          {t.downloads.note}{" "}
          <button
            onClick={() => setTermsOpen(true)}
            data-testid="downloads-terms-link"
            className="text-blue-500 underline-offset-2 hover:underline"
          >
            {t.terms.link}
          </button>
        </p>
      </div>
    </section>
  );
};

export default Downloads;
